import React, { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Search, UploadCloud, Plus, Minus, Camera } from "lucide-react";
import BottomNav from "../../components/BottomNav.tsx";
import { digitalOceanImageUrl } from "../../utils/digitalOcean.ts";

interface WorkStationData {
  id: number;
  name: string;
  pdf: File | null;
}

// WorkStation Component
const WorkStation: React.FC<{
  id: number;
  onComplete: (id: number, hasDocument: boolean) => void;
}> = ({ id, onComplete }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(true);
  const [stationName, setStationName] = useState("");
  const [workInstructionPdf, setWorkInstructionPdf] = useState<File | null>(null);

  const handleStationClick = () => {
    if (!isEditing) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStationName(e.target.value);
  };

  const handleNameBlur = () => {
    setIsEditing(false);
    onComplete(id, workInstructionPdf !== null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setIsEditing(false);
      onComplete(id, workInstructionPdf !== null);
    }
  };

  const handlePdfUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setWorkInstructionPdf(event.target.files[0]);
      onComplete(id, true);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto mb-4">
      <div 
        className="bg-blue-50 p-4 rounded-lg cursor-pointer"
        onClick={handleStationClick}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {isEditing ? (
              <input
                value={stationName}
                onChange={handleNameChange}
                onBlur={handleNameBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                placeholder="Name of Work Station"
                className="w-full px-2 py-1 rounded border border-gray-300 focus:outline-none focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div 
                className="text-blue-900 font-medium"
                onClick={handleNameClick}
              >
                {stationName || "Name of Work Station"}
              </div>
            )}
          </div>
          {isExpanded ? (
            <Minus className="w-5 h-5" />
          ) : (
            <Plus className="w-5 h-5" />
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 border border-gray-200 rounded-lg mt-2">
          <label htmlFor={`pdfUpload-${id}`} className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-6">
            <div className="text-center">
              {workInstructionPdf ? (
                <span className="text-gray-600">{workInstructionPdf.name}</span>
              ) : (
                <>
                  <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="mt-2">
                    <span className="text-sm text-gray-600">
                      Upload a pdf of the work instruction under this station
                    </span>
                  </div>
                </>
              )}
            </div>
          </label>
          <input
            type="file"
            id={`pdfUpload-${id}`}
            className="hidden"
            accept="application/pdf"
            onChange={handlePdfUpload}
          />
        </div>
      )}
    </div>
  );
};

// Main Component
const AddWorkStation: React.FC = () => {
  const { modelId, phase } = useParams<{ modelId: string; phase: string }>();
  const navigate = useNavigate();
  const [modelImage, setModelImage] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [workStations, setWorkStations] = useState<number[]>([]);
  const [completedStations, setCompletedStations] = useState<Set<number>>(new Set());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("New Work Stations:", { modelId, phase, workStations });
    navigate(`/vehicle/${modelId}`);
  };

  const handleAddWorkStation = () => {
    if (workStations.length === 0 || Array.from(completedStations).includes(workStations[workStations.length - 1])) {
      setWorkStations([...workStations, Date.now()]);
    }
  };

  const handleStationComplete = (id: number, hasDocument: boolean) => {
    if (hasDocument) {
      setCompletedStations(new Set(completedStations.add(id)));
    }
  };

  return (
    <div className="min-h-screen bg-blue-900 pb-20 flex flex-col items-center">
      {/* Header */}
      <div className="p-4 flex justify-between items-center w-full max-w-5xl">
        <h1 className="text-white text-xl font-bold">{modelId} | {(phase || "").replace("-", " ")}</h1>
        <img src={digitalOceanImageUrl("new%20logos/Asset%204.png")} alt="KMC Logo" className="w-16 h-auto" />
      </div>
      <header className="max-w-4xl mx-auto flex items-center justify-between py-2">
             <h1 className="text-2xl text-white font-bold font-family-poppins font-semibold text-center flex-1">Welcome Phiona!</h1>
      
      </header>

      {/* Search Bar */}
            <div className="px-6 mb-6 w-full">
              <div className="relative ">
                <input
                  type="text"
                  placeholder="Search for an instruction document..."
                  className="w-full py-3 px-4 pr-10 rounded-full"
                />
                <Search className="absolute  right-4 top-1/2 transform -translate-y-1/2 text-gray-900" />
              </div>
            </div>

      {/* Main Content */}
      <div className="bg-white h-screen w-full p-6 rounded-t-3xl flex-1">
        <div className="max-w-2xl mx-auto lg">
          <h2 className="text-blue-900 text-3xl font-bold mb-4">Body Shop Instructions</h2>
          <div className="flex flex-cols gap-5">
          {/* Description Input */}
          <textarea
            placeholder="Add a short description of the new model"
            className="w-full p-4 border rounded-lg mb-6"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          {/* Image Upload */}
          <div className="flex justify-center mb-8">
            <div className="text-center">
              {modelImage ? (
                <img
                  src={modelImage}
                  alt="Model"
                  className="w-32 h-32 rounded-full object-cover mb-2"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-2">
                  <Camera className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <p className="text-gray-600">Add a Model Image</p>
            </div>
          </div>
          </div>
          {/* Work Stations Section */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-blue-900 text-xl font-bold">Work Stations</h3>
              <button
                type="button"
                onClick={handleAddWorkStation}
                className="bg-blue-900 text-white px-4 py-2 rounded-full hover:bg-blue-800"
              >
                Add Work Station
              </button>
            </div>

            {workStations.map((id) => (
              <WorkStation 
                key={id} 
                id={id}
                onComplete={handleStationComplete}
              />
            ))}
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            className=" bg-blue-900 text-white p-3 rounded-full hover:bg-blue-800"
          >
            Save Work Stations
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav showBack={true} />
    </div>
  );
};

export default AddWorkStation;